import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { OPTION_CATALOG } from '@roborally/engine';
import { api, ApiError, logSince } from '../api.js';
import type { CourseLayout, GameSettings as Settings, GameView, LogEntry, PlayerRow } from '../api.js';
import { Board } from '../components/Board.js';
import { BoardKey } from '../components/BoardKey.js';
import { GameSettings } from '../components/GameSettings.js';
import { LogPanel } from '../components/LogPanel.js';
import { RegisterStrip } from '../components/RegisterStrip.js';
import { RobotStats } from '../components/RobotStats.js';
import { useGameReplay } from '../components/useGameReplay.js';
import { archiveSquare } from '../components/useSteppedPlayback.js';
import { botSet, colourMap, nameMap } from '../identity.js';

/**
 * A finished game, watched from turn 1.
 *
 * Everything drawn here was recorded as it happened: the frames say where
 * the robots stood and the events say what they did. No rule is
 * re-evaluated, the engine is not involved, and the server is asked for
 * nothing but the log -- so a game played under older rules replays as it
 * was played rather than as it would go today.
 *
 * Its own page rather than a mode of `Game`, which is wired throughout
 * for a live game: a stream, a clock, submissions, and a playback that
 * follows state arriving. None of that has anything to do with watching a
 * game that is over.
 *
 * A game whose frames do not carry the token layer is REFUSED rather than
 * played thinly. Debris is laid with no event recording it, so a board
 * that was not written down cannot be recovered by any amount of reading,
 * and a replay that quietly leaves out what everyone was driving around
 * is worth less than an honest refusal.
 */

/**
 * A transport control's face, drawn rather than typed.
 *
 * There is no pause character in the fonts this ships, and where one
 * exists at all it arrives as an emoji at the wrong size and colour. Two
 * rectangles are two rectangles everywhere, they take the button's own
 * colour through `currentColor`, and they keep the source to ordinary
 * characters.
 */
function Transport({ shape }: { shape: 'rewind' | 'back' | 'play' | 'pause' | 'forward' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="currentColor"
      aria-hidden="true"
    >
      {/* A step is a triangle with a bar on the far side of it, which is
          how every player tells a step from a run. */}
      {/* A register back is two triangles behind the bar. */}
      {shape === 'rewind' && (
        <>
          <rect x="3" y="5" width="2.5" height="14" />
          <path d="M13.5 5 L6.5 12 L13.5 19 Z" />
          <path d="M21.5 5 L14.5 12 L21.5 19 Z" />
        </>
      )}
      {shape === 'back' && (
        <>
          <rect x="5" y="5" width="2.5" height="14" />
          <path d="M20 5 L9.5 12 L20 19 Z" />
        </>
      )}
      {shape === 'forward' && (
        <>
          <path d="M4 5 L14.5 12 L4 19 Z" />
          <rect x="16.5" y="5" width="2.5" height="14" />
        </>
      )}
      {shape === 'play' && <path d="M6 4 L20 12 L6 20 Z" />}
      {shape === 'pause' && (
        <>
          <rect x="6" y="4" width="4" height="16" />
          <rect x="14" y="4" width="4" height="16" />
        </>
      )}
    </svg>
  );
}

export function Replay() {
  const { gameId } = useParams();
  const id = Number(gameId);
  const navigate = useNavigate();

  const [view, setView] = useState<GameView | null>(null);
  const [layout, setLayout] = useState<CourseLayout | null>(null);
  const [players, setPlayers] = useState<PlayerRow[]>([]);
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      const [v, l, p, log, s] = await Promise.allSettled([
        api.gameView(id), api.courseLayout(id), api.players(id), logSince(id, 0),
        api.gameSettings(id),
      ]);
      // Left out quietly when it cannot be had, as on the game screen.
      if (s.status === 'fulfilled') setSettings(s.value);
      if (v.status === 'fulfilled') setView(v.value);
      if (l.status === 'fulfilled') setLayout(l.value);
      if (p.status === 'fulfilled') setPlayers(p.value.players);
      if (log.status === 'fulfilled') setEntries(log.value);
      if (v.status === 'rejected') {
        setError(v.reason instanceof ApiError ? v.reason.message : 'Could not load the game.');
      } else if (log.status === 'rejected') {
        setError('Could not load this game\'s log.');
      }
      setLoading(false);
    })();
  }, [id]);

  const replay = useGameReplay(entries);

  const names = useMemo(() => nameMap(players), [players]);
  const bots = useMemo(() => botSet(players), [players]);
  const nameOf = (robotId: string) => names.get(robotId) ?? robotId;
  const colours = useMemo(
    () => colourMap(players, view?.robots ?? []),
    [players, view],
  );

  /**
   * The robots as they stood at this beat.
   *
   * Built from the finished game's robots so the required fields are all
   * there, then overwritten with what the frame recorded. Everything the
   * board draws comes from the replay: a power-down, a virtual return or
   * an archive marker read off the FINAL state would be a fact about the
   * end of the game shown over the middle of it.
   */
  const robots = useMemo(() => {
    if (!view) return [];
    if (!replay.positions) return view.robots;
    return view.robots.map((r) => {
      const at = replay.positions?.get(r.id);
      if (!at) return { ...r, destroyed: true };
      const was = replay.flags.get(r.id);
      return {
        ...r,
        x: at.x,
        y: at.y,
        facing: at.facing,
        damage: at.damage,
        destroyed: false,
        // Absent on a frame written before these were recorded, which
        // reads as off -- the same answer as for a game that had none.
        eliminated: was?.eliminated ?? false,
        virtual: was?.virtual ?? false,
        poweredDown: was?.poweredDown ?? false,
        // As the frame had it, not the finished game's: the count goes up
        // as the register after the touch starts.
        lastTouchedFlag: was?.lastTouchedFlag ?? r.lastTouchedFlag,
        // Taken at step E and drawn from then, and after that wherever
        // this beat has the flag it sits on -- riding the belt with it
        // rather than standing still until the next frame moves it.
        archiveMarker: archiveSquare(
          replay.archives.get(r.id)
            ?? { archiveMarker: was?.archiveMarker, archiveFlag: was?.archiveFlag },
          replay.courseFlags,
        ),
      };
    });
  }, [view, replay.positions, replay.flags, replay.courseFlags, replay.archives]);

  /**
   * How far each robot had got BY THIS BEAT, counted off the log.
   *
   * `lastTouchedFlag` on the finished game is where a robot ended, which
   * over turn 3 would be the answer to the game. The log says when each
   * flag was taken instead: a `flagTouched` per flag, and the last flag
   * as a `victory`, which is how the engine records it -- two robots can
   * claim it at once, and both of them touched it.
   *
   * Counted over whole ENTRIES rather than beats, as the log panel reads
   * them: a flag is credited while the entry that took it is showing.
   */
  const flagsReached = useMemo(() => {
    const finalFlag = layout?.flags.find((f) => f.isFinal)?.number;
    // From the frame first: a robot that starts on flag 1, or joins level
    // with the table, is credited with that flag without ever touching it,
    // and the frame is the only record of it. The events below then raise
    // it the moment a flag is taken, a register before the frame would.
    const reached = new Map<string, number>();
    for (const [id, was] of replay.flags) {
      if ((was.lastTouchedFlag ?? 0) > 0) reached.set(id, was.lastTouchedFlag!);
    }
    // Newest first, which a maximum does not care about.
    for (const played of replay.played) {
      for (const event of played.events) {
        const n = event.type === 'flagTouched' ? event.count
          : event.type === 'victory' ? finalFlag
            : undefined;
        if (n !== undefined && n > (reached.get(event.robotId) ?? 0)) {
          reached.set(event.robotId, n);
        }
      }
    }
    return reached;
  }, [replay.played, replay.flags, layout]);

  if (loading) {
    return (
      <main className="content">
        <p className="eyebrow">Loading the game</p>
      </main>
    );
  }

  if (!view || !layout) {
    return (
      <main className="content">
        <p className="notice">{error ?? 'That game could not be loaded.'}</p>
        <button className="secondary" onClick={() => navigate('/')}>Lobby</button>
      </main>
    );
  }

  const entry = replay.entry;

  return (
    <main className="game">
      {/* The board and its key in one stack, so the side panel's height
          cannot come between them -- see `.game-main`. */}
      <div className="game-main">
        <div className="game-board-pane">
          {/* `tokens`: what the frame recorded, never the final layer --
              that is the one thing that must not be drawn over an earlier
              turn. The finished board is for the game that cannot be
              played at all, which has no frame to draw instead. */}
          <Board
            layout={layout}
            robots={robots}
            colours={colours}
            youAre={view.youAre}
            epoch={`${entry?.turn ?? 0}:${entry?.register ?? entry?.phase ?? ''}`}
            nameOf={nameOf}
            bots={bots}
            tokens={replay.positions ? (replay.tokens ?? []) : view.tokens}
            shot={replay.shot}
            // The flags as the frames recorded them, walked along by the
            // belts. Undefined where the frames do not say, and the
            // course's own are drawn -- which is right for a course whose
            // flags never move and the best available for a game played
            // before they were recorded.
            flags={(replay.positions ? replay.courseFlags : view.flags) ?? undefined}
            wrecked={replay.wrecked}
            // Playing again jumps from the last beat back to turn 1, which
            // is a cut. Every beat after it is a move, and animates.
            cut={`run${replay.run}`}
            cellPx={44}
          />
        </div>

        <BoardKey
          elements={layout.elements ?? []}
          tokens={replay.positions ? (replay.tokens ?? []) : view.tokens}
          debris={view.debris}
          turnMode={view.turnMode === true}
        />
        {settings && <GameSettings settings={settings} boards={layout?.boards} flags={layout?.flags.length} />}
      </div>

      <aside className="game-side">
        <div className="game-heading">
          <p className="eyebrow">{layout.name}</p>
          <p className="mono muted" style={{ fontSize: '0.8125rem' }}>
            {entry
              ? (
                <>
                  Turn <span className="num">{entry.turn ?? 0}</span>
                  {entry.register !== null && (
                    <>{', register '}<span className="num">{entry.register}</span></>
                  )}
                </>
              )
              : `${entries.length === 0 ? 'Nothing was played' : 'Not started'}`}
          </p>
        </div>

        <div className="spread">
          {replay.boardRecorded ? (
            <div className="replay-transport">
              {/* One beat at a time either side of the play button: a
                  step, not a register. Both stop the replay, so a step
                  is not immediately overwritten by the next tick. */}
              <button
                onClick={replay.registerBack}
                disabled={!replay.canStepBack}
                title="Back one register"
                aria-label="Back one register"
              >
                <Transport shape="rewind" />
              </button>
              <button
                onClick={replay.stepBack}
                disabled={!replay.canStepBack}
                title="Back one step"
                aria-label="Back one step"
              >
                <Transport shape="back" />
              </button>
              {/* Play is play whether it is starting, resuming or going
                  round again -- the title says which, since the face
                  cannot. */}
              <button
                onClick={replay.playing ? replay.pause : replay.play}
                title={replay.playing ? 'Pause' : replay.done ? 'Play again' : 'Play'}
                aria-label={replay.playing ? 'Pause' : replay.done ? 'Play again' : 'Play'}
              >
                <Transport shape={replay.playing ? 'pause' : 'play'} />
              </button>
              <button
                onClick={replay.stepForward}
                disabled={!replay.canStepForward}
                title="Forward one step"
                aria-label="Forward one step"
              >
                <Transport shape="forward" />
              </button>
            </div>
          ) : <span />}
          <button className="secondary" onClick={() => navigate('/')}>Lobby</button>
        </div>

        {/* Refused rather than played thinly. A frame written before the
            board was recorded holds the robots and nothing else, and
            debris was laid with no event to rebuild it from, so what
            would play is a course with everything swept off it. */}
        {!replay.boardRecorded && (
          <p className="notice">
            {entries.length === 0
              ? 'This game has no log to play.'
              : 'This game was played before the board was recorded, so it '
                + 'cannot be replayed. Only the robots were written down, '
                + 'not the debris and tokens they were driving around.'}
          </p>
        )}

        {/* How far through, which a replay of several minutes needs and
            the log panel cannot show: it scrolls with what has been
            played, not with what is left. */}
        {replay.boardRecorded && (
          <div className="replay-progress">
            <div style={{ width: `${Math.round(replay.progress * 100)}%` }} />
          </div>
        )}

        {error && <p className="notice">{error}</p>}

        {/* The roster the live game keeps, filled in from the replay
            rather than from the finished game.

            EVERY FIGURE HERE IS A FIGURE THE FRAME RECORDED, or one
            counted off the events -- damage, the archive, powered down,
            virtual, the lives and the option count from the frame, the
            flag from the log, the registers as far as the frame had them
            turned up. A frame recorded before the lives, the options or
            the registers went into it shows none of them, rather than the
            finished game's: a robot's final program shown over turn 2 is
            the same mistake as its final position would be. That is also
            why the fields are listed one by one instead of spreading
            `robot`, which carries the finished game's figures along with
            the replayed ones. */}
        <div className="stack" style={{ gap: '0.4rem' }}>
          <p className="eyebrow">Robots</p>
          {/* The viewer's own robot first where they played in this game,
              as the live roster orders it. A stable sort, so everybody
              else keeps turn order. */}
          {[...robots]
            .sort((a, b) => (
              Number(b.id === view.youAre) - Number(a.id === view.youAre)
            ))
            .map((robot) => {
              const was = replay.flags.get(robot.id);
              // A robot destroyed this turn keeps the registers it played.
              const strip = was ?? replay.fallen.get(robot.id);
              return (
                <div key={robot.id}>
                  <div className="standing">
                    <span className="swatch" style={{ background: colours.get(robot.id) }} />
                    <span style={{ fontWeight: robot.id === view.youAre ? 600 : 400 }}>
                      {nameOf(robot.id)}
                    </span>
                    <RobotStats
                      robot={{
                        damage: robot.damage,
                        destroyed: robot.destroyed,
                        lastTouchedFlag: flagsReached.get(robot.id) ?? 0,
                        archiveMarker: robot.archiveMarker,
                        virtual: robot.virtual,
                        poweredDown: robot.poweredDown,
                        lives: was?.lives,
                        options: was?.options,
                      }}
                    />
                  </div>
                  {/* Named from the catalog: the frame's Options carry no
                      effect, and the finished game's lack any given up. */}
                  {strip?.registers && (
                    <RegisterStrip
                      registers={strip.registers}
                      current={entry?.register ?? 0}
                      options={OPTION_CATALOG}
                      locked={strip.lockedRegisters}
                      inactive={strip.inactiveForRegister}
                      inactiveBy={strip.inactiveBy}
                      moveOptions={strip.moveOptions}
                    />
                  )}
                </div>
              );
            })}
        </div>

        <LogPanel
          entries={replay.played}
          robotIds={view.robots.map((r) => r.id)}
          youAre={view.youAre}
          nameOf={nameOf}
        />
      </aside>
    </main>
  );
}
